import React, { useEffect, useState } from 'react';
import { getLocationAttributesForSegment } from './utils';
import {
  getFormattedAddress,
  getFormattedCityStateZip,
} from '../locations/utils/address';
import {
  getGoogleMapsLink,
  getStaticGoogleMapUrl,
} from '../locations/utils/googleMaps';
import { getOpenStatus, formatDates } from '../locations/utils/workHours';
import FFLinkButton from '../../buttons/FFLinkButton';
import AlarmClockIcon from '../../icons/AlarmClockIcon';
import DirectionsIcon from '../../icons/DirectionsIcon';
import FridgeWithPinIcon from '../../icons/FridgeWithPinIcon';
import PinIcon from '../../icons/PinIcon';
import RetailWithPinIcon from '../../icons/RetailWithPinIcon';
import useSiteMetadata from '../../../utils/useSiteMetaData';
import imagePlaceholder from '../../../utils/imagePlaceholder';
import useSegment, { EVENTS } from '../../../utils/useSegment';
import styles from './AddressHoursSection.module.css';

/**
 *
 * @param {object} props
 * @param {FF.Location__LDP} props.locationData
 */
export default function AddressHoursSection({ locationData }) {
  const { googleMapsApiKey } = useSiteMetadata();
  const { pushTrackEvent } = useSegment();

  const isFridge = locationData.type === 'FRIDGE';
  const isRetail = locationData.type === 'WHOLESALE';

  const hasHours = Boolean(locationData.locationConfigs.workHours);

  let identifiedLocationType;
  if (isFridge) {
    identifiedLocationType = 'Fridge';
  } else if (isRetail) {
    identifiedLocationType = 'Retail';
  }

  const parsedWorkHours =
    locationData.locationConfigs.workHours &&
    JSON.parse(locationData.locationConfigs.workHours);
  const openHoursStatus =
    parsedWorkHours &&
    locationData.locationConfigs.timeZone &&
    getOpenStatus(parsedWorkHours, locationData.locationConfigs.timeZone)
      ?.message;

  const hoursListing = hasHours ? formatDates(parsedWorkHours, true) : null;

  // need to wait until page load to generate URL, because we need to read the user's "devicePixelRatio"
  const [googleMapsImageUrl, setGoogleMapsImageUrl] =
    useState(imagePlaceholder);
  useEffect(() => {
    setGoogleMapsImageUrl(
      getStaticGoogleMapUrl(locationData, googleMapsApiKey),
    );
  }, []);
  const GoogleMapsImage = (
    <img
      alt={`Map showing the ${
        identifiedLocationType ? `${identifiedLocationType} ` : ''
      }location`}
      className={styles.googleMapsImage}
      height="428"
      width="640"
      loading="lazy"
      src={googleMapsImageUrl}
    />
  );

  return (
    <section className={styles.wrapper}>
      <h2 className={styles.header2Normal}>
        {`${identifiedLocationType ? `${identifiedLocationType} ` : ''}Address`}
        {hasHours && (
          <span className={styles.mainHeaderHoursText}> & Hours</span>
        )}
      </h2>
      <div className={styles.content}>
        <div className={styles.addressAndHours}>
          <div className={styles.address}>
            {isFridge && <FridgeWithPinIcon className={styles.addressIcon} />}
            {isRetail && <RetailWithPinIcon className={styles.addressIcon} />}
            <div>
              <p className={styles.addressText}>
                {getFormattedAddress(locationData)}
              </p>
              <p className={styles.addressText}>
                {getFormattedCityStateZip(locationData)}
              </p>
            </div>
          </div>
          <div className={styles.directions}>
            <PinIcon className={styles.directionsPinIcon} />
            <span className={styles.subtitle1}>
              {locationData.operationConfigs.directions}
            </span>
          </div>
          <div className={styles.mobileMapImage}>{GoogleMapsImage}</div>
          <FFLinkButton
            onClick={() => {
              pushTrackEvent(
                EVENTS.GET_DIRECTIONS,
                getLocationAttributesForSegment(locationData),
              );
            }}
            buttonType="tertiary"
            className={styles.directionsLink}
            href={getGoogleMapsLink(locationData)}
          >
            Get Directions
            <DirectionsIcon />
          </FFLinkButton>
          {hasHours && (
            <div className={styles.hours}>
              <h2 className={styles.hoursHeaderText}>Hours</h2>
              <div className={styles.hoursContent}>
                <AlarmClockIcon className={styles.hoursClockIcon} />
                <div>
                  {openHoursStatus && (
                    <p className={styles.openHoursStatus}>{openHoursStatus}</p>
                  )}
                  {hoursListing && (
                    <ul className={styles.hoursListing}>
                      {hoursListing.map(hrs => (
                        <li className={styles.hoursListingItem} key={hrs}>
                          {hrs}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.desktopMapImage}>{GoogleMapsImage}</div>
      </div>
    </section>
  );
}
